// import node_modules
import * as PropTypes from 'prop-types';
import * as React from 'react';
import { Heading, LinkButton, Spacer, Text } from '@storylinerlabs/design';
import { useTranslation } from 'react-i18next';

// import types
import { TemplateGenreSlugEnum, TemplateGenre } from '@storylinerlabs/templates';

// import component
import Column from '../atoms/Column';
import EarlyAccessSection from '../elements/EarlyAccessSection';
import GenresGgrid from '../grids/GenresGrid';
import LinkComponent from '../atoms/LinkComponent';
import LogosGrid from '../grids/LogosGrid';
import MainLayout from '../layouts/MainLayout';
import SEO from '../meta/SEO';
import Section from '../atoms/Section';

// define page
const GenresPage: React.FunctionComponent<{
  hrefLang: string;
  templateGenres: TemplateGenre[];
}> = ({ hrefLang, templateGenres }) => {
  // init hooks
  const { t } = useTranslation('genres');

  // init render
  return (
    <>
      <SEO
        description={t('seo.description')}
        hrefLang={hrefLang}
        image={t('seo.image')}
        title={t('seo.title')}
      />

      <MainLayout>
        {/* Hero */}
        <Section>
          {/* Navbar */}
          <Spacer height="64px" />
          <Spacer mobileHeight="25px" tabletHeight="50px" height="75px" />
        </Section>
        <Section>
          <Column cols="ml">
            <Heading alignment="center">{t('hero.title')}</Heading>
            <Text alignment="center" size="large">
              {t('hero.intro')}
            </Text>
          </Column>
        </Section>

        {/* Genres */}
        <Section
          bgImage={t('genres.dividerTop')}
          bgImageDark={t('genres.dividerTopDark')}
          bgPosition="center top"
        >
          <Spacer mobileHeight="100px" tabletHeight="125px" height="175px" />
        </Section>
        <Section bgColor="desertStorm">
          <Column cols="ml">
            <Heading alignment="center" tag="h2">
              {t('genres.title')}
            </Heading>
          </Column>
        </Section>
        <GenresGgrid bgColor="desertStorm" templateGenres={templateGenres} />
        <Section bgColor="desertStorm">
          <Column>
            <Spacer height="50px" />
          </Column>
        </Section>

        {/* Innovation partner */}
        <Section bgColor="allports">
          <Spacer height="75px" />
        </Section>
        <Section bgColor="allports">
          <Column cols="prose">
            <Heading alignment="center" tag="h3" isWhite>
              {t('innovationPartner.title')}
            </Heading>
            <Text alignment="center" isWhite>
              {t('innovationPartner.intro')}
            </Text>
          </Column>
        </Section>
        <Section bgColor="allports">
          <Column cols="lg">
            <LogosGrid
              bgColor="allports"
              logos={t('innovationPartner.logos', { returnObjects: true })}
            />
          </Column>
        </Section>
        <Section bgColor="allports">
          <Spacer height="36px" />
        </Section>
        <Section bgColor="allports">
          <LinkComponent href={t('innovationPartner.callToAction.link')}>
            <LinkButton
              color="naked"
              iconRight="ri-mail-send-line"
              size="large"
              text={t('innovationPartner.callToAction.label')}
              textIsWhite
            />
          </LinkComponent>
          <Spacer height="25px" />
        </Section>
        <Section
          bgImage={t('innovationPartner.dividerBottom')}
          bgImageDark={t('innovationPartner.dividerBottomDark')}
          bgPosition="center bottom"
        >
          <Spacer mobileHeight="100px" tabletHeight="125px" height="175px" />
        </Section>

        {/* Call to action  */}
        <Section>
          <Spacer height="100px" />
        </Section>
        <EarlyAccessSection />
        <Section>
          <Spacer height="100px" />
        </Section>
      </MainLayout>
    </>
  );
};

GenresPage.propTypes = {
  hrefLang: PropTypes.string.isRequired,
  templateGenres: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.oneOf(Object.values(TemplateGenreSlugEnum)).isRequired,
      coverImageSquared: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
};

export default GenresPage;
